/* PlanSummary — read-only visual view of a case's per-tooth plan.
 *
 * iter88: planner-facing view (`viewer="planner"`) of the doctor's
 *   structured plan, reading from `case.prescription`.
 * iter89: same component now serves the doctor side too
 *   (`viewer="doctor"`); identical data shape so a prescription-shaped
 *   object works equally well.
 * iter91 (Wave F): the planner can now author per-tooth detail directly
 *   on the case plan. Pass EITHER `prescription`, `plan`, or both. When
 *   both are populated, the planner's `plan` wins (it's the authored
 *   output the lab manufactures from). `prescription` stays as the
 *   fallback for doctor-self-plan cases where the doctor authored on
 *   the prescription, not the plan.
 *
 * Renders 4 charts (movements / attachments / cuts / ramps), each only
 * if populated. All read-only — this component never edits.
 */
import React from "react";
import DentalChart from "./DentalChart";


function pickPrimarySource(plan, prescription) {
    // iter91 Wave F — return whichever source actually carries per-tooth
    // data. Plan wins when populated. Doctor-self-plan cases keep
    // rendering via prescription fallback.
    const hasPlanData = !!(plan && (
        (plan.tooth_movements_plan || []).length
        || (plan.attachments_plan || []).length
        || (plan.precision_cuts || []).length
        || plan.bite_ramps?.enabled
    ));
    return hasPlanData ? plan : (prescription || null);
}

const ANGLE_CLASS_LABEL = (v) => v
    .replace("class_i", "Class I")
    .replace("class_ii", "Class II")
    .replace("class_iii", "Class III")
    .replace("mixed", "Mixed")
    .replace("cusp_to_cusp", "Cusp-to-cusp");

const DIRECTION_INITIAL = {
    mesial: "M", distal: "D", buccal: "B", lingual: "L",
    intrusion: "↓", extrusion: "↑", rotation_cw: "↻", rotation_ccw: "↺",
};

const PURPOSE_INITIAL = {
    rotation: "R", extrusion: "X", intrusion: "I",
    root_control: "RC", anchor: "A", other: "·",
};

const FDI_TEETH = [
    "18", "17", "16", "15", "14", "13", "12", "11",
    "21", "22", "23", "24", "25", "26", "27", "28",
    "48", "47", "46", "45", "44", "43", "42", "41",
    "31", "32", "33", "34", "35", "36", "37", "38",
];

// iter89 — titles adapt by viewer. The data is the same; the framing
// changes. "Doctor's …" reads naturally when the planner is reviewing;
// "Treatment …" reads naturally when the doctor is viewing their own
// (or the planner's) authored plan.
const TITLES = {
    planner: {
        movements: "Doctor's tooth-by-tooth movement plan",
        attachments: "Doctor's attachment plan",
        cuts: "Doctor's precision cuts",
        ramps: "Doctor's bite ramp plan",
    },
    doctor: {
        movements: "Tooth-by-tooth movement plan",
        attachments: "Attachment plan",
        cuts: "Precision cuts",
        ramps: "Bite ramp plan",
    },
};

function ChartLegend({ items }) {
    if (!items?.length) return null;
    return (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground mt-2">
            {items.map((i) => (
                <span key={i.k}>
                    <span className="font-mono font-bold">{i.k}</span> = {i.v}
                </span>
            ))}
        </div>
    );
}

function MovementsCard({ movements, title }) {
    if (!movements?.length) return null;
    const byTooth = Object.fromEntries(movements.map((m) => [m.tooth, m]));
    return (
        <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3 sm:p-4" data-testid="plan-summary-movements">
            <div className="flex items-baseline justify-between gap-2 mb-2">
                <h4 className="font-display text-sm font-bold text-violet-900">{title}</h4>
                <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">
                    {movements.length} {movements.length === 1 ? "tooth" : "teeth"}
                </span>
            </div>
            <DentalChart
                teeth={FDI_TEETH}
                marked={byTooth}
                accent="violet"
                renderBadge={(t) => byTooth[t]?.directions?.length
                    ? DIRECTION_INITIAL[byTooth[t].directions[0]] || "·"
                    : null}
                testidPrefix="plan-summary-movements-chart"
            />
            <ChartLegend items={[
                { k: "M/D", v: "Mesial/Distal" },
                { k: "B/L", v: "Buccal/Lingual" },
                { k: "↓/↑", v: "Intrude/Extrude" },
                { k: "↻/↺", v: "Rotate" },
            ]} />
            <details className="mt-3">
                <summary className="text-[11px] font-semibold cursor-pointer text-violet-800 hover:text-violet-900">
                    Show per-tooth detail ({movements.length})
                </summary>
                <ul className="mt-2 space-y-1 text-[11px]">
                    {movements.map((m) => (
                        <li
                            key={m.tooth}
                            className="font-mono bg-white/70 rounded px-2 py-1 border border-violet-100"
                            data-testid={`plan-summary-movement-row-${m.tooth}`}
                        >
                            <span className="font-bold">{m.tooth}</span>
                            {" · "}
                            <span>{(m.directions || []).join(", ") || "—"}</span>
                            {m.amount_mm !== undefined && m.amount_mm !== null && m.amount_mm !== "" && (
                                <span> · {m.amount_mm}mm</span>
                            )}
                            {m.rotation_deg !== undefined && m.rotation_deg !== null && m.rotation_deg !== "" && (
                                <span> · rot {m.rotation_deg}°</span>
                            )}
                            {m.torque_deg !== undefined && m.torque_deg !== null && m.torque_deg !== "" && (
                                <span> · torque {m.torque_deg}°</span>
                            )}
                            {m.anchorage && <span> · {m.anchorage} anchor</span>}
                            {m.notes && <span className="block text-muted-foreground font-sans mt-0.5">{m.notes}</span>}
                        </li>
                    ))}
                </ul>
            </details>
        </div>
    );
}

function AttachmentsCard({ attachments, title }) {
    if (!attachments?.length) return null;
    const byTooth = Object.fromEntries(attachments.map((a) => [a.tooth, a]));
    return (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 sm:p-4" data-testid="plan-summary-attachments">
            <div className="flex items-baseline justify-between gap-2 mb-2">
                <h4 className="font-display text-sm font-bold text-primary">{title}</h4>
                <span className="text-[10px] font-semibold text-primary/80 uppercase tracking-wider">
                    {attachments.length} {attachments.length === 1 ? "tooth" : "teeth"}
                </span>
            </div>
            <DentalChart
                teeth={FDI_TEETH}
                marked={byTooth}
                accent="primary"
                renderBadge={(t) => byTooth[t]?.purpose ? PURPOSE_INITIAL[byTooth[t].purpose] || "·" : null}
                testidPrefix="plan-summary-attachments-chart"
            />
            <ChartLegend items={[
                { k: "R", v: "Rotation" },
                { k: "X", v: "Extrusion" },
                { k: "I", v: "Intrusion" },
                { k: "RC", v: "Root ctrl" },
                { k: "A", v: "Anchor" },
            ]} />
        </div>
    );
}

function PrecisionCutsCard({ cuts, title }) {
    if (!cuts?.length) return null;
    const byTooth = Object.fromEntries(cuts.map((c) => [c.tooth, c]));
    return (
        <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-3 sm:p-4" data-testid="plan-summary-cuts">
            <div className="flex items-baseline justify-between gap-2 mb-2">
                <h4 className="font-display text-sm font-bold text-amber-900">{title}</h4>
                <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">
                    {cuts.length}
                </span>
            </div>
            <DentalChart
                teeth={FDI_TEETH}
                marked={byTooth}
                accent="amber"
                renderBadge={(t) => byTooth[t]?.position ? byTooth[t].position[0].toUpperCase() : null}
                testidPrefix="plan-summary-cuts-chart"
            />
            <ChartLegend items={[
                { k: "B", v: "Buccal" },
                { k: "L", v: "Lingual" },
                { k: "M", v: "Mesial" },
                { k: "D", v: "Distal" },
            ]} />
        </div>
    );
}

function BiteRampsCard({ ramps, title }) {
    if (!ramps?.enabled) return null;
    const teeth = ramps.teeth?.length ? ramps.teeth.join(", ") : "auto by position";
    return (
        <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-3 sm:p-4" data-testid="plan-summary-ramps">
            <h4 className="font-display text-sm font-bold text-rose-900 mb-1">{title}</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-1 gap-x-4 text-[12px]">
                <div>
                    <dt className="font-semibold text-rose-900/70 uppercase tracking-wider text-[10px]">Position</dt>
                    <dd className="font-medium">{(ramps.position || "—").replace(/_/g, " ")}</dd>
                </div>
                <div>
                    <dt className="font-semibold text-rose-900/70 uppercase tracking-wider text-[10px]">Teeth</dt>
                    <dd className="font-mono">{teeth}</dd>
                </div>
                {ramps.notes && (
                    <div className="sm:col-span-3 mt-1">
                        <dt className="font-semibold text-rose-900/70 uppercase tracking-wider text-[10px]">Notes</dt>
                        <dd className="text-foreground/85">{ramps.notes}</dd>
                    </div>
                )}
            </dl>
        </div>
    );
}

export default function PlanSummary({ prescription, plan, viewer = "planner" }) {
    // iter91 Wave F — accept both prescription + plan. The selected
    // primary source becomes the source for ALL four charts so we
    // never mix doctor-authored attachments with planner-authored
    // movements. If you need merge-semantics later, add explicit
    // per-chart picking here.
    const source = pickPrimarySource(plan, prescription);
    if (!source) return null;
    const movements = source.tooth_movements_plan || [];
    const attachments = source.attachments_plan || [];
    const cuts = source.precision_cuts || [];
    const ramps = source.bite_ramps;

    const hasAnything = movements.length || attachments.length || cuts.length || ramps?.enabled;
    if (!hasAnything) return null;

    // iter91 — adapt the titles when the doctor is viewing planner-authored
    // data (vs reviewing their own intake). For the planner viewer, titles
    // stay framed around "Doctor's …" since the planner is still reviewing
    // the doctor's intake on the Doctor's Submission tab.
    const isPlannerAuthored = source === plan;
    const t = TITLES[viewer] || TITLES.planner;
    // For doctor viewing planner-authored content, drop the "Planner's" prefix —
    // the surrounding "Treatment plan" iteration card already establishes
    // authorship + context, making prefixes redundant noise.
    const titles = (viewer === "doctor" && isPlannerAuthored)
        ? {
            movements: "Tooth-by-tooth movement",
            attachments: "Attachments",
            cuts: "Precision cuts",
            ramps: "Bite ramps",
        }
        : t;

    return (
        <div className="space-y-3" data-testid="plan-summary">
            <MovementsCard movements={movements} title={titles.movements} />
            <AttachmentsCard attachments={attachments} title={titles.attachments} />
            <PrecisionCutsCard cuts={cuts} title={titles.cuts} />
            <BiteRampsCard ramps={ramps} title={titles.ramps} />
        </div>
    );
}

